import { LitElement, html } from 'lit';
import { styles } from './css';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { imagebossUrl } from '@Helpers/imageboss';
import { logErrorToSentry } from '../../../../helpers/helpers';
import { fireAdobeAnalyticsEvent } from '../../../../helpers/adobe-analytics';

import '@Root/lib/lit-3form-block/components/cta-text';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';

const getInit = { method: 'GET', headers: { Accept: 'application/json' } };

export class VisualizeProductsBarrel extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      expanded: { type: Boolean },
      _swatchesPerPage: { type: Number },
      _selectedHardwareFinish: { type: Object },
      _selectedPattern: { type: Object },
      _currentPatternSwatches: { type: Array },
      _hardwareOptions: { type: Array },
      _isLoadingHardwareOptions: { type: Boolean },
      _filterUrlQueryString: { type: String },
      _totalResults: { type: Number },
      _totalPages: { type: Number },
      _isLoadingCurrentResults: { type: Boolean },
      _searchString: { type: String },
      _currentPagePath: { type: String },
      _loadedPages: { type: Object },
      _hasLoadedInitialOptions: { type: Boolean },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));

    this._swatchesPerPage = 24;
    this._totalResults = 0;
    this._totalPages = 0;
    this._lastFetchedPagePath = '';
    this._selectedHardwareFinish = {};
    this._selectedPattern = {};
    this._allPatternSwatches = [];
    this._currentPatternSwatches = [];
    this._hardwareOptions = [];
    this._filterUrlQueryString = '';
    this._isLoadingCurrentResults = false;
    this._searchString = '';
    this._currentPagePath = '';
    this._loadedPages = {};
    this._hasLoadedInitialOptions = false;
    this._isLoadingHardwareOptions = false;
  }

  get showHardwareOptions() {
    return this._hardwareOptions.length > 0;
  }

  get showPatternOptions() {
    return !!this._currentPatternSwatches?.length;
  }

  async connectedCallback() {
    super.connectedCallback();
    this.expanded = true;

    try {
      await this.fetchThreekitStages();
    } catch (error) {
      logErrorToSentry(error);
    }

    this.addEventListener('scrollToVisualize', e => {
      this.expanded = true;
    });
  }

  render() {
    return html`
      <div
        class=${classMap({
          'barrel-wrapper': true,
          expanded: this.expanded,
          'print-none': this.hideInPrint,
        })}
      >
        <div
          id="visualization-barrel-header"
          class=${classMap({
            'barrel-header': true,
            hide: this._expanded,
          })}
        >
          <span class="barrel-heavy-text">
            <span>PRODUCTS</span>
          </span>
        </div>
        ${this.patternOptionsHTML}
        ${this.showHardwareOptions ? this.hardwareOptionsHTML : html``}
      </div>
    `;
  }

  get barrelHeaderIcon() {
    return this.expanded
      ? '/static/icons/SVG/minimize-dash.svg'
      : '/static/icons/SVG/expand-plus.svg';
  }

  get hideInPrint() {
    return this.content.hide_in_print;
  }

  toggleVisbility() {
    this.expanded = !this.expanded;
    if (this.expanded) {
      const eventData = {
        type: 'click',
        detail: {
          eVar8: `INP|Barrel Expanded|Products`,
        },
      };
      fireAdobeAnalyticsEvent(eventData);
    }
  }

  requestAQuote() {
    const requestMoreInfoFromBarrel = new CustomEvent(
      'requestMoreInfoFromBarrel',
      {
        bubbles: true,
        composed: true,
        detail: {
          cta: 'Request a Quote',
          url: '/request-quote',
        },
      }
    );
    this.dispatchEvent(requestMoreInfoFromBarrel);
  }

  get primaryCtaData() {
    return JSON.stringify({
      text: 'Request a Quote',
      url: '/request-quote',
      fullWidth: true,
    });
  }

  get hardwareOptionsHTML() {
    return html`
      <div>
        <div class="hardware-options-wrapper">
          <div class="hardware-options-header">
            <p class="barrel-heavy-text">
              Hardware Finishes
            </p>
          </div>
          <div class="swatch-group">
            <p class="swatch-group-header">
              Standard Finishes
            </p>
            <div class="swatches">
              ${this._hardwareOptions.map(option => {
                if (option.isStandard) {
                  return this.renderOptionsSwatch(option);
                }
              })}
            </div>
          </div>
          <div class="swatch-group">
            <p class="swatch-group-header">
              Premium Finishes (Additional Cost)
            </p>
            <div class="swatches">
              ${this._hardwareOptions.map(option => {
                if (!option.isStandard) {
                  return this.renderOptionsSwatch(option);
                }
              })}
            </div>
          </div>
          ${this._isLoadingHardwareOptions
            ? html`
                <div class="no-results">
                  <p class="description">Loading hardware color options.</p>
                </div>
              `
            : html``}
        </div>
      </div>
    `;
  }

  renderOptionsSwatch(option) {
    if (option.image_src) {
      // Interlayer Swatches
      return html`
        <div
          class="swatch-wrapper"
          @click="${() => this.updateSelectedProduct(option)}"
        >
          <span
            class=${classMap({
              'image-swatch': true,
              'selected-swatch': this.isSelectedPattern(option),
              'not-selected-swatch': !this.isSelectedPattern(option),
            })}
          >
            <img
              src=${imagebossUrl(option.image_src, {
                height: '250',
                width: '200',
              })}
            />
          </span>
          <p class="swatch-name">${this.configuredSwatchName(option)}</p>
        </div>
      `;
    }
    if (option.color) {
      // Hardware Swatches
      return html`
        <div
          class="swatch-wrapper"
          @click="${() => this.updateSelectedHardwareFinish(option)}"
        >
          <span
            class=${classMap({
              'color-swatch': true,
              'selected-swatch': this.isSelectedHardwareFinish(option),
              'not-selected-swatch': !this.isSelectedHardwareFinish(option),
            })}
          >
            <span
              class=${classMap({
                'needs-border': option.needsBorder,
              })}
              style="${styleMap(this.swatchBackgroundStyle(option))}"
            ></span>
          </span>
          <p class="swatch-name">${this.configuredSwatchName(option)}</p>
        </div>
      `;
    }
  }

  configuredSwatchName(swatch) {
    if (swatch.display_name) {
      return swatch.display_name;
    } else {
      const name = swatch.name ? swatch.name : '';
      return swatch.parentName ? `${swatch.parentName} ${name}` : `${name}`;
    }
  }

  isSelectedPattern(option) {
    return (
      Object.keys(this._selectedHardwareFinish).length > 0 &&
      option.id === this._selectedPattern.id
    );
  }

  isSelectedHardwareFinish(option) {
    return (
      Object.keys(this._selectedHardwareFinish).length > 0 &&
      option.id === this._selectedHardwareFinish.id
    );
  }

  async updateSelectedProduct(option) {
    option.render_interlayer_id = this.content.render_interlayer_id;
    option.name = this.configuredSwatchName(option);
    this._selectedPattern = option;

    try {
      await this.fetchThreekitHardwareOptions(option.id);
    } catch (error) {
      logErrorToSentry(error);
    }

    const visualizedProductSelectedEvent = new CustomEvent(
      'visualizedProductSelected',
      {
        detail: {
          option,
        },
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(visualizedProductSelectedEvent);
  }

  updateSelectedHardwareFinish(option) {
    this._selectedHardwareFinish = option;
    const hardwareColorSelectedEvent = new CustomEvent(
      'hardwareColorSelected',
      {
        detail: {
          option,
        },
        bubbles: true,
        composed: true,
      }
    );
    this.dispatchEvent(hardwareColorSelectedEvent);
  }

  swatchBackgroundStyle(option) {
    const color = option.hex_code ? option.hex_code : option.color;
    return {
      'background-color': `#${color}`,
    };
  }

  get patternOptionsHTML() {
    return html`
      <div class="pattern-options-wrapper">
        ${this.showPatternOptions
          ? html`
              <div class="swatches">
                ${this._currentPatternSwatches.map(option => {
                  return this.renderOptionsSwatch(option);
                })}
              </div>
            `
          : html``}
        ${this._isLoadingCurrentResults
          ? html`
              <div class="no-results">
                <p class="description">Loading product options.</p>
              </div>
            `
          : html``}
        ${!this.showPatternOptions && !this._isLoadingCurrentResults
          ? html`
              <div class="no-results">
                <p class="description">No results found. Please try again.</p>
              </div>
            `
          : html``}
      </div>
    `;
  }

  updateCurrentPatternSwatches() {
    this._currentPatternSwatches = Object.values(
      this._loadedPages
    )[0]?.threekit_stages;
    this._isLoadingCurrentResults = false;
    if (!this._hasLoadedInitialOptions) {
      this._hasLoadedInitialOptions = true;
    }
    if (this._currentPatternSwatches?.length) {
      this.updateSelectedProduct(this._currentPatternSwatches[0]);
    }
  }

  async fetchThreekitHardwareOptions(stageId) {
    this._isLoadingHardwareOptions = true;
    const basePath = this.content.threekit_hardware_finishes_path;
    const path = `${basePath}&threekit_stage_id=${stageId}`;
    fetch(path, getInit)
      .then(response => {
        if (response.ok) {
          this._isLoadingHardwareOptions = false;
          return response.json();
        }
      })
      .then(async json => {
        this.setHardwareOptions(json);
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  setHardwareOptions(data) {
    const hardwareFinishOptions = data?.threekit_hardware_finishes || [];
    this._hardwareOptions = [];
    if (hardwareFinishOptions.length) {
      if (hardwareFinishOptions.length) {
        data.threekit_hardware_finishes.forEach(finish => {
          if (finish.display_name && finish.color) {
            finish['isStandard'] = finish.color.toLowerCase() === 'c5c5c5';
            finish['needsBorder'] = finish.display_name
              .toLowerCase()
              .includes('white');
            if (
              this.content.threekit_stage_id === 13 &&
              finish.color.toLowerCase() === '0d0d0d'
            ) {
              // Black Oxide is standard only for Suspend
              finish['isStandard'] = true;
            }

            this._hardwareOptions.push(finish);
          }
        });

        if (this._hardwareOptions.length) {
          // Sort the isStandard finishes to the front of the array
          this._hardwareOptions.sort((a, b) => {
            return (b.isStandard === true) - (a.isStandard === true);
          });
        }
      }
    }

    if (this._hardwareOptions.length > 0) {
      let newHardwareOptionIndex = 0;

      // Persist the user's selected color option when a new system is selected
      if (this._selectedHardwareFinish?.color) {
        const similarColorIndex = this._hardwareOptions.findIndex(option => {
          return option.color === this._selectedHardwareFinish.color;
        });
        if (similarColorIndex >= 0) newHardwareOptionIndex = similarColorIndex;
      }

      this.updateSelectedHardwareFinish(
        this._hardwareOptions[newHardwareOptionIndex]
      );
    }
  }

  async fetchThreekitStages() {
    this._isLoadingCurrentResults = true;
    const [basePath, baseQueryParams] = this.content.threekit_stages_path.split(
      '?'
    );

    const threekitParams = new URLSearchParams(baseQueryParams);
    threekitParams.set(
      'threekit_hardware_finish_id',
      this._selectedHardwareFinish.id
    );

    let variantUrl = `${basePath}?${threekitParams}`;

    this._loadedPages = {};
    this._currentPagePath = variantUrl;

    await this.loadProductsPage(variantUrl || basePath)
      .then(json => {
        if (!!json) {
          this._totalPages = json.total_pages || 0;
          this.setVariantOptions(json, variantUrl);
        }
        this._isLoadingCurrentResults = false;
      })
      .then(() => {
        this.updateCurrentPatternSwatches();
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  loadProductsPage(productsPagePath) {
    return fetch(productsPagePath, getInit)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  setVariantOptions(data, path) {
    this._loadedPages[path] = data;
  }
}
customElements.define('visualize-products-barrel', VisualizeProductsBarrel);
